import { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { ClockIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import useAccountQuery from 'api/queries/useAccountQuery';
import useSelectionsQuery from 'api/queries/useSelectionsQuery';
import { PLUS_MEMBERSHIP_FT_CART_SAVINGS_BANNER_DISMISSED } from 'constants/plusMembership';
import { coinWithStar } from 'features/paid-membership/PromotionalBanners/shared/BannerIcons';
import PromotionalBannerBase from 'features/paid-membership/PromotionalBanners/shared/PromotionalBannerBase';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import { trackEvent } from 'utils/analyticsUtils';
import { getTrialDaysRemainingCopy } from 'utils/plusMembershipUtils';
import {
    getCartMemberSavings,
    getCartNonMemberSavings,
    getFreeShippingThresholdMet,
    PriceType,
} from 'utils/priceUtils';

/**
 * Member cart savings banner. Paid members see the gradient savings card; free-trial users with
 * the Jalapeño `featureMPlusForAllPhase1` flag see a cranberry savings + trial countdown (inline and sticky).
 *
 * @param {Object} [props]
 * @param {boolean} [props.hide=false] - When `sticky`, hides the bar off-screen instead of “sticking”
 * @param {IntersectionObserverEntry | undefined} [props.intersectionEntry] - Drives when the user has scrolled past the banner
 * @param {Ref<HTMLDivElement> | undefined} [props.intersectionRef] - Attached to the non-sticky root for intersection observation
 * @param {boolean} [props.savingsBannerInView=false] - For the non-sticky trial layout, used with view tracking when the cart column is in view
 * @param {boolean} [props.sticky=false] - Renders the bottom sticky variant (with dismiss)
 * @returns {ReactNode | null | false}
 */
function PlusMembershipMemberBanner({
    hide = false,
    intersectionEntry,
    intersectionRef,
    savingsBannerInView = false,
    sticky = false,
} = {}) {
    const [trialBannerDismissed, setTrialBannerDismissed] = useState(
        localStorage.getItem(PLUS_MEMBERSHIP_FT_CART_SAVINGS_BANNER_DISMISSED)
    );
    const [belowBanner, setBelowBanner] = useState(false);
    const impressionSentRef = useRef(false);

    const { data: account = {} } = useAccountQuery();
    const { data: selections = {} } = useSelectionsQuery();
    const { plusMembershipBranding, plusMembershipEligibleOrder, plusMembershipShortBranding, shippingFees, trial } =
        useAccountPlusMembershipHook();
    const isPlusMembershipFTForAllPhase1Enabled = !!account.featureFlags?.featureMPlusForAllPhase1;

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;
    const freeShippingThresholdMet = getFreeShippingThresholdMet({
        account,
        options: { priceType },
        selections,
    });
    const { plusMembershipShippingSavings } = shippingFees ?? {};
    const freeShippingWithMembership =
        plusMembershipEligibleOrder && !freeShippingThresholdMet && plusMembershipShippingSavings > 0;

    const cartNonMemberSavings = getCartNonMemberSavings({ account, selections });
    const cartMemberShippingSavings = freeShippingWithMembership ? plusMembershipShippingSavings : 0;
    const cartFreeShippingSavings = freeShippingThresholdMet ? (account.shippingFee ?? 0) : 0;
    const cartMemberSavings = getCartMemberSavings({ account, selections }) + cartMemberShippingSavings;
    const cartTotalSavings = cartNonMemberSavings + cartMemberSavings + cartFreeShippingSavings;

    const trialEndDate = isPlusMembershipFTForAllPhase1Enabled ? trial?.endDate : null;
    const trialDaysRemainingCopy = getTrialDaysRemainingCopy({
        trialEndDate,
        plusMembershipShortBranding,
    });

    const showTrialCartBanner = !!trialEndDate && cartMemberSavings > 0 && plusMembershipEligibleOrder;
    const shouldShowTrialBanner = showTrialCartBanner && !(sticky && (trialBannerDismissed || belowBanner));

    useEffect(() => {
        setBelowBanner(intersectionEntry?.boundingClientRect?.top < 0);
    }, [intersectionEntry]);

    useEffect(() => {
        if (!shouldShowTrialBanner || impressionSentRef.current) return;
        const isVisible = sticky ? !hide : savingsBannerInView;
        if (!isVisible) return;
        trackEvent('plus membership view cart savings banner');
        impressionSentRef.current = true;
    }, [shouldShowTrialBanner, sticky, hide, savingsBannerInView]);

    if (!plusMembershipEligibleOrder) {
        return null;
    }

    if (showTrialCartBanner) {
        if (!shouldShowTrialBanner) {
            return false;
        }

        const dismissBanner = () => {
            setTrialBannerDismissed(true);
            localStorage.setItem(PLUS_MEMBERSHIP_FT_CART_SAVINGS_BANNER_DISMISSED, true);
            trackEvent('plus membership dismiss cart savings banner');
        };

        const bannerContent = (
            <div className="rounded-lg bg-cranberry p-10 text-center text-white if:bg-blueberry">
                <p className="text-body-sm font-grotesk-bold leading-[20px]!">
                    You&apos;re saving ${cartMemberSavings.toFixed(2)} on this order with {plusMembershipShortBranding}
                </p>
                {trialDaysRemainingCopy && (
                    <p className="text-body-xs mt-5 flex items-center justify-center gap-5 font-grotesk">
                        <ClockIcon className="h-15 w-15 shrink-0" aria-hidden />
                        <span>{trialDaysRemainingCopy}</span>
                    </p>
                )}
            </div>
        );

        if (sticky) {
            return (
                <div className={clsx('z-10', hide ? 'overflow-hidden' : 'sticky bottom-20 m-20')}>
                    <div className={clsx('transition', hide ? 'translate-y-full' : 'translate-y-0')}>
                        <button
                            type="button"
                            onClick={dismissBanner}
                            className="absolute -top-15 left-10 rounded-full bg-off-white"
                            aria-label="Close banner"
                        >
                            <XMarkIcon className="h-25 w-25 text-black" />
                        </button>
                        {bannerContent}
                    </div>
                </div>
            );
        }

        return (
            <div ref={intersectionRef} className="mt-10 overflow-hidden rounded-lg">
                {bannerContent}
            </div>
        );
    }

    if (sticky) {
        return null;
    }

    const heading = (
        <span className={clsx('relative', cartMemberSavings > 0 ? 'top-10' : 'top-20')}>
            <span className="text-heading-xs">${cartTotalSavings.toFixed(2)}</span>
            <span className="text-body-sm relative bottom-[2px]"> saved on this order</span>
        </span>
    );

    const subheading =
        cartMemberSavings > 0 ? (
            <span className="text-body-xs relative top-[5px]">
                <span className="font-grotesk-bold text-cranberry">${cartMemberSavings.toFixed(2)}</span> is from your{' '}
                {plusMembershipBranding} membership
            </span>
        ) : null;

    if (cartNonMemberSavings <= 0 && cartMemberSavings <= 0) {
        return null;
    }

    return <PromotionalBannerBase heading={heading} subheading={subheading} iconRight={coinWithStar} hideCta />;
}

export { PlusMembershipMemberBanner };
